import React, { useState } from "react";
import { toast } from "sonner";
import { Languages, Loader2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LANG_LABELS = {
  it: "Italiano",
  en: "English",
  he: "עברית",
  ru: "Русский",
};

const T = {
  it: {
    trigger: "Traduci",
    dialogTitle: "Traduci documento",
    targetLabel: "Traduci in",
    preview: "Anteprima traduzione",
    translating: "Traduzione in corso…",
    download: "Scarica PDF tradotto",
    cache: "Cache: risultato istantaneo",
    disclaimer:
      "Traduzione automatica di riferimento. Fa fede il documento originale firmato.",
    close: "Chiudi",
    translate: "Traduci",
    error: "Errore di traduzione",
  },
  en: {
    trigger: "Translate",
    dialogTitle: "Translate document",
    targetLabel: "Translate to",
    preview: "Translation preview",
    translating: "Translating…",
    download: "Download translated PDF",
    cache: "Cache: instant result",
    disclaimer: "Machine translation. The signed original prevails.",
    close: "Close",
    translate: "Translate",
    error: "Translation error",
  },
  he: {
    trigger: "תרגם",
    dialogTitle: "תרגם מסמך",
    targetLabel: "תרגם ל",
    preview: "תצוגת תרגום",
    translating: "מתרגם…",
    download: "הורד PDF מתורגם",
    cache: "מטמון: תוצאה מיידית",
    disclaimer: "תרגום אוטומטי לעיון בלבד.",
    close: "סגור",
    translate: "תרגם",
    error: "שגיאת תרגום",
  },
  ru: {
    trigger: "Перевести",
    dialogTitle: "Перевести документ",
    targetLabel: "Перевести на",
    preview: "Предпросмотр",
    translating: "Перевод…",
    download: "Скачать переведённый PDF",
    cache: "Кэш: мгновенный результат",
    disclaimer: "Машинный перевод. Приоритет — подписанный оригинал.",
    close: "Закрыть",
    translate: "Перевести",
    error: "Ошибка перевода",
  },
};

async function sha256Hex(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Small icon-only button that opens a modal to translate an attached PDF.
 *  Props:
 *    kind: "mandate" | "intermediation" | "file"
 *    id: mandate.id / intermediation_contract.id / files.file_id
 *    label (optional): overrides "Traduci"
 *    variant: "icon" (default) | "full" — full shows label
 */
export default function TranslateButton({ kind, id, variant = "icon", className = "" }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Default to a language different from the current UI lang to avoid same-lang round-trip.
  const [target, setTarget] = useState(lang === "en" ? "it" : "en");
  const [preview, setPreview] = useState("");
  const [cacheKey, setCacheKey] = useState(null);
  const [cacheHit, setCacheHit] = useState(false);
  const [history, setHistory] = useState([]);
  const tt = T[lang] || T.en;

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/documents/translate/history", { params: { kind, id } });
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    }
  };

  const run = async () => {
    setBusy(true);
    setPreview("");
    setCacheKey(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("id", id);
      fd.append("target_lang", target);
      const { data } = await api.post("/documents/translate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setPreview(data.preview || "");
      setCacheHit(!!data.cache_hit);
      setCacheKey(data.translated_file_id);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tt.error);
    } finally {
      setBusy(false);
    }
  };

  const loadHistoryItem = async (item) => {
    setTarget(item.target_lang);
    setCacheKey(item.file_id);
    setCacheHit(true);
    setBusy(true);
    setPreview("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("id", id);
      fd.append("target_lang", item.target_lang);
      const { data } = await api.post("/documents/translate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      setPreview(data.preview || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || tt.error);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!cacheKey) return;
    try {
      const resp = await api.get(`/files/${cacheKey}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `translated-${target}-${cacheKey.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tt.error);
    }
  };

  const isRtl = target === "he";

  return (
    <>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          setOpen(true);
          setPreview("");
          setCacheKey(null);
          loadHistory();
        }}
        data-testid={`translate-btn-${kind}-${id}`}
        className={`bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full ${className}`}
        title={tt.trigger}
      >
        <Languages className="h-3.5 w-3.5" />
        {variant === "full" && <span className="ms-1.5">{tt.trigger}</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-2xl max-h-[85vh] flex flex-col"
          data-testid="translate-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-light"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {tt.dialogTitle}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-3 py-2">
            <label className="text-xs text-[#7A6E5A]">{tt.targetLabel}</label>
            <Select value={target} onValueChange={setTarget} disabled={busy}>
              <SelectTrigger
                data-testid="translate-target-select"
                className="w-40 bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LANG_LABELS).map(([code, label]) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={run}
              disabled={busy}
              data-testid="translate-run-btn"
              className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full disabled:opacity-40"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {tt.translating}
                </>
              ) : (
                <>
                  <Languages className="h-4 w-4 me-2" />
                  {tt.translate}
                </>
              )}
            </Button>
            {cacheHit && (
              <span
                className="text-[10px] tracking-widest uppercase text-emerald-700"
                data-testid="translate-cache-hit"
              >
                {tt.cache}
              </span>
            )}
          </div>

          {history.length > 0 && (
            <div className="py-1" data-testid="translate-history">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A7A5E] mb-1.5">
                {lang === "it" ? "Traduzioni precedenti" : lang === "he" ? "תרגומים קודמים" : lang === "ru" ? "Прошлые переводы" : "Past translations"}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {history.map((h) => (
                  <button
                    key={h.cache_key}
                    type="button"
                    onClick={() => loadHistoryItem(h)}
                    data-testid={`translate-history-${h.target_lang}`}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-[#D8CFBB] bg-[#FBF8F1] text-[#4A3F30] hover:bg-[#F5EDDA] hover:border-[#C5A880] transition-colors"
                    title={new Date(h.created_at).toLocaleString()}
                  >
                    {LANG_LABELS[h.target_lang] || h.target_lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div
            className="flex-1 overflow-auto rounded-md border border-[#D8CFBB] bg-[#FBF8F1] p-4 my-2 min-h-[200px]"
            data-testid="translate-preview-box"
            dir={isRtl ? "rtl" : "ltr"}
          >
            {busy ? (
              <div className="h-full flex items-center justify-center text-[#7A6E5A] text-sm">
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                {tt.translating}
              </div>
            ) : preview ? (
              <pre
                className="whitespace-pre-wrap text-sm text-[#2E2A22] font-sans leading-relaxed"
                data-testid="translate-preview-text"
              >
                {preview}
              </pre>
            ) : (
              <p className="text-sm text-[#9A8B72] italic">
                {tt.preview}…
              </p>
            )}
          </div>

          <p className="text-[11px] text-[#9A8B72] italic">{tt.disclaimer}</p>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-[#4A3F30]"
            >
              {tt.close}
            </Button>
            {cacheKey && !busy && (
              <Button
                onClick={download}
                data-testid="translate-download-btn"
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"
              >
                <Download className="h-4 w-4 me-2" />
                {tt.download}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
